import { useRef, useEffect, useCallback, useMemo } from 'react';

import type { RunMapStop, RunOverlaySet, ClickedRunStop } from '../runTypes';
import { createRunStopOverlays } from '../overlays/createRunStopOverlays';
import { createRunRouteOverlays } from '../overlays/createRunRouteOverlays';

// OPTIONS ------------------------------------------------------------------- //

interface UseRunOverlaysOptions {
  map: google.maps.Map | null;
  stops: RunMapStop[];
  /** Encoded Google polylines for each leg (index i = leg from stop i to stop i+1) */
  legPolylines?: (string | null)[];
  showGeofences?: boolean;
  /** Index of the selected stop (0-based into stops array) — highlights marker + adjacent legs */
  selectedStopIndex?: number | null;
  onClickStop?: (stop: ClickedRunStop | null) => void;
}

// HOOK ---------------------------------------------------------------------- //

/**
 * Manages all map overlays for a mobility run: stop markers, geofences,
 * and route polylines connecting stops in sequence.
 * Follows the same ref-based pattern as useMoveOverlays.
 */
export function useRunOverlays({
  map,
  stops,
  legPolylines,
  showGeofences = true,
  selectedStopIndex,
  onClickStop,
}: UseRunOverlaysOptions) {
  const overlayRef = useRef<RunOverlaySet | null>(null);
  const animationFrameIdsRef = useRef<number[]>([]);
  const clickListenersRef = useRef<google.maps.MapsEventListener[]>([]);

  const prefersReducedMotion = useMemo(
    () => typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches,
    [],
  );

  // ── Cleanup ──

  const clearAllOverlays = useCallback(() => {
    animationFrameIdsRef.current.forEach(id => cancelAnimationFrame(id));
    animationFrameIdsRef.current = [];

    clickListenersRef.current.forEach(l => google.maps.event.removeListener(l));
    clickListenersRef.current = [];

    if (overlayRef.current) {
      overlayRef.current.stopMarkers.forEach(m => m.setMap(null));
      overlayRef.current.geofences.forEach(g => g.setMap(null));
      overlayRef.current.routePolylines.forEach(p => p.setMap(null));
      overlayRef.current.routeAnimated?.setMap(null);
      overlayRef.current = null;
    }
  }, []);

  // ── Geofence visibility at high zoom ──

  const showGeofencesRef = useRef(showGeofences);
  showGeofencesRef.current = showGeofences;

  useEffect(() => {
    if (!map) return;
    const SATELLITE_ZOOM = 19;

    const updateGeofenceVisibility = () => {
      const zoom = map.getZoom() ?? 10;
      const visible = zoom < SATELLITE_ZOOM && showGeofencesRef.current;
      overlayRef.current?.geofences.forEach(g => g.setVisible(visible));
    };

    const listener = map.addListener('zoom_changed', updateGeofenceVisibility);
    return () => google.maps.event.removeListener(listener);
  }, [map]);

  // ── Build overlays ──

  useEffect(() => {
    if (!map || stops.length === 0) {
      clearAllOverlays();
      return;
    }

    clearAllOverlays();

    // 1. Stop markers + geofences
    const stopResult = createRunStopOverlays({
      map,
      stops,
      selectedStopIndex: selectedStopIndex ?? null,
      onClickStop,
      clickListeners: clickListenersRef.current,
    });

    // 2. Route polylines
    const routeResult = createRunRouteOverlays({
      map,
      stops,
      legPolylines,
      selectedStopIndex: selectedStopIndex ?? null,
      prefersReducedMotion,
    });

    animationFrameIdsRef.current = routeResult.animationFrameIds;

    overlayRef.current = {
      stopMarkers: stopResult.markers,
      geofences: stopResult.geofences,
      routePolylines: routeResult.polylines,
      routeAnimated: routeResult.animatedPolyline,
    };

    return () => {
      clearAllOverlays();
    };
  }, [map, stops, legPolylines, selectedStopIndex, clearAllOverlays, onClickStop, prefersReducedMotion]);

  // ── Toggle geofence visibility from prop ──

  useEffect(() => {
    overlayRef.current?.geofences.forEach(g => g.setVisible(showGeofences));
  }, [showGeofences]);

  // ── Cleanup on unmount ──

  useEffect(() => {
    return () => {
      clearAllOverlays();
    };
  }, [clearAllOverlays]);
}
